/**
 * Reading a trigger→relay join's `endpointConfig` whatever casing it was written in.
 *
 * The blob is a firmware contract (Shared/Models/Api/v1/ClientEndpoints/WebRelayEndpointConfig) and
 * the device parses it with Newtonsoft, which matches property names case-insensitively — so
 * `Relays` / `Relay` / `StateToSend` work on a truck exactly as well as `relays` / `relay` /
 * `stateToSend`. The generated TypeScript model does not: it only looks for the camelCase names.
 * A PascalCase blob (the server wrote those when a driver provisioned an alert from the cab)
 * therefore opened in the dashboard's trigger editor as "no relays", the editor filled the gap with
 * a blank "relay 1, Off" row, and a save stored that row NEXT TO the real one under a second key.
 *
 * So everything is folded to camelCase here, both list keys included, in the order the device
 * reads them: Postgres stores the blob as jsonb, which orders `Relays` ahead of `relays`, and the
 * device appends each list it meets to the same collection.
 *
 * Import-free on purpose, like relayWiring.ts: callers wrap the result in the generated model.
 */

export type PlainWebRelayConfig = {
    relay: number;
    stateToSend: number;
    expression: string;
    pulseTime?: number;
    $type?: string;
};

export type PlainWebRelayEndpointConfig = {
    relays: PlainWebRelayConfig[];
    $type?: string;
};

/**
 * The Evaluation a relay entry needs before the device will close the terminal at all. The firmware
 * skips an entry whose expression is blank outright (WebRelayClientEndpointHandler: "a relay with
 * no expression has nothing to evaluate"), so a row saved without one is a relay that never fires.
 * Mirrors the server's `RelayWiring.FireExpression` and the console's `RELAY_FIRE_EXPRESSION` —
 * if you change one, change all three.
 */
export const WEB_RELAY_FIRE_EXPRESSION = "true";

type AnyRecord = Record<string, unknown>;

const isRecord = (v: unknown): v is AnyRecord => typeof v === "object" && v !== null && !Array.isArray(v);

/** The value under `name` in either casing; camelCase wins, as the editor has always read it. */
const either = (entry: AnyRecord, name: string): unknown =>
    entry[name] ?? entry[name.charAt(0).toUpperCase() + name.slice(1)];

const asNumber = (v: unknown): number | undefined => (typeof v === "number" && Number.isFinite(v) ? v : undefined);

function normalizeEntry(raw: unknown): PlainWebRelayConfig | undefined {
    if (!isRecord(raw)) return undefined;
    const entry: PlainWebRelayConfig = {
        relay: asNumber(either(raw, "relay")) ?? 0,
        stateToSend: asNumber(either(raw, "stateToSend")) ?? 0,
        expression: typeof either(raw, "expression") === "string" ? (either(raw, "expression") as string) : "",
    };
    const pulseTime = asNumber(either(raw, "pulseTime"));
    if (pulseTime !== undefined) entry.pulseTime = pulseTime;
    if (typeof raw.$type === "string") entry.$type = raw.$type;
    return entry;
}

const listUnder = (blob: AnyRecord, key: string): unknown[] => (Array.isArray(blob[key]) ? (blob[key] as unknown[]) : []);

/**
 * Parse a relay join config into camelCase entries. A blank, unreadable or non-object blob is an
 * empty config rather than a throw: the editor renders on every keystroke of a draft.
 */
export function normalizeWebRelayEndpointConfig(raw: string | null | undefined): PlainWebRelayEndpointConfig {
    let parsed: unknown;
    try {
        parsed = raw ? JSON.parse(raw) : undefined;
    } catch {
        parsed = undefined;
    }
    if (!isRecord(parsed)) return { relays: [] };

    const relays = [...listUnder(parsed, "Relays"), ...listUnder(parsed, "relays")]
        .map(normalizeEntry)
        .filter((e): e is PlainWebRelayConfig => e !== undefined);

    const config: PlainWebRelayEndpointConfig = { relays };
    if (typeof parsed.$type === "string") config.$type = parsed.$type;
    return config;
}
